import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Chip, Grid, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, Checkbox, Alert, CircularProgress, Tooltip, FormControlLabel,
  Fab, Badge, Dialog, DialogTitle, DialogContent, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import StatusChip from '../../components/StatusChip';
import { InvTextField, InvSelect } from '../../components/InvField';
import { getPrescription, updateIpdPrescriptionStatus } from '../../api/pharmacy';
import { getStores } from '../../api/stockLedger';
import { formatCurrency, formatDate } from '../../utils';
import {
  mapPrescription,
  mapPrescriptionLines,
  extractPatientClinicalAlerts,
  prepareGenericSearchTerm,
} from '../../utils/pharmacyMappers';
import {
  STOCK_STATUS,
  applyMatchedItemToLine,
  enrichItemUom,
  fetchPrescribeSearch,
  resolveNewMedicineOrigin,
  resolvePrescribeLineStock,
} from '../../utils/pharmacyAvailability';
import { MEDICINE_ORIGIN, buildNewMedicineDraft, medicineNameKey } from '../../utils/newMedicineEntity';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS, INV_SUITE_BASE } from '../../utils/inventoryRoles';
import { isPendingPrescriptionStatus } from '../../utils/prescriptionStatus';
import {
  loadOrderDispenseLedger,
  runCreateVerifyReserve,
  runCreateVerifyControlled,
} from '../../utils/pharmacyDispenseFlow';
import { priorDispenseForLine, priorDispenseUomMismatch } from '../../utils/dispenseOrderLink';
import { isControlledOrNarcotic, buildPharmacistAuthorization } from '../../utils/controlledDrug';
import {
  groupLinesForShortClose,
  mergeStatusUpdateOutcomes,
  rejectedReasonsText,
  shortCloseLineCount,
  shortCloseReasonForStock,
  statusUpdateOutcome,
} from '../../utils/orderStatusUpdate';
import {
  readPreferredPharmacyStore,
  writePreferredPharmacyStore,
} from '../../utils/pharmacyStorePreference';
import { PHARMACY_SHORT_CLOSE_REASONS } from '../../constants';
import {
  issueUomFactor, issueUomCode, baseUomCode, issueQtyIssue, baseQtyHint,
  totalIssuableQty, formatIssuableQty,
} from '../../utils/uomConversionEntity';
import ControlledDrugAuthModal from '../../components/ControlledDrugAuthModal';
import SubstitutePickerDialog from './components/SubstitutePickerDialog';
import OrderReasonDialog from './components/OrderReasonDialog';
import BulkShortCloseDialog from './components/BulkShortCloseDialog';
import { IPD_COLORS, cardSx } from '../../theme';
import { fadeInSx, lineTableSx, summaryHighlightSx } from './pharmacyStyles';
import { DetailSummaryGrid, SummaryStat, PatientClinicalAlertsBox } from './PharmacyUi';

const actionStripSx = {
  width: { xs: '100%', md: 92 },
  flexShrink: 0,
  display: 'flex',
  flexDirection: { xs: 'row', md: 'column' },
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2.5,
  py: { xs: 2, md: 4 },
  px: { xs: 2, md: 0 },
  borderRadius: '18px',
  background: `linear-gradient(180deg, ${IPD_COLORS.selectedBg} 0%, ${IPD_COLORS.cardBg} 100%)`,
  border: `1px solid ${IPD_COLORS.selectedBorder}`,
  boxShadow: IPD_COLORS.shadow.card,
  alignSelf: 'stretch',
};

const fabButtonSx = (bg) => ({
  backgroundColor: bg,
  color: '#fff',
  boxShadow: IPD_COLORS.shadow.cardHover,
  transition: 'transform 0.2s ease',
  '&:hover': { backgroundColor: bg, transform: 'scale(1.08)' },
});

function buildLinesFromItems(items, ledger = null) {
  return items
    .map((i) => {
      // THREE measurements of the same units — never a sum. The order book's
      // delivered_quantity is dead while the gateway's credit hooks are reverted
      // (backend ac3c9e8); the ward's accepted figure (details.accepted_quantity)
      // is a floor under what was handed over, and survives even when the
      // dispensing service cannot be read; the dispense ledger is the pharmacy's
      // own record. A 0 from any of them means only that IT does not know, so the
      // highest is the informed answer.
      const prior = priorDispenseForLine(ledger, i);
      const dispensed = Math.max(
        Number(i.dispensed_qty) || 0,
        Number(i.accepted_qty) || 0,
        prior?.qty || 0,
      );
      return { ...i, dispensed_qty: dispensed, prior_dispense: prior };
    })
    // A line whose prescribed quantity is a real number drops out once it is
    // filled. One whose quantity is this module's ESTIMATE never does — closing a
    // row against a guessed denominator would silently end a therapy the doctor
    // is still expecting. It stays, with its history on show and the box blank.
    .filter((i) => !i.qty_specified || (i.prescribed_qty - i.dispensed_qty) > 0)
    .map((i) => ({
      ...i,
      selected: true,
      controlled: isControlledOrNarcotic(i),
      // Left blank when the prescriber never stated a quantity. `prescribed_qty` is
      // an estimate in that case (see resolvePrescribedQty), and pre-filling it made
      // a guess look like an order — the pharmacist would dispense 1 tablet, or 10,
      // because that is what the box already said.
      dispense_qty: i.qty_specified ? i.prescribed_qty - i.dispensed_qty : '',
      batch_number: i.batch_number || '',
      unit_rate: i.unit_rate || '',
      uom_id: i.uom_id || '',
      controlled_auth_id: i.controlled_auth_id || null,
    }));
}

export default function StartDispensePage() {
  const history = useHistory();
  const { prescriptionId } = useParams();
  const { url } = useRouteMatch();
  const backUrl = url.replace(`/start-dispense/${prescriptionId}`, '/active-dispenses');

  const [prescription, setPrescription] = useState(null);
  const [lines, setLines] = useState([]);
  const [storeRows, setStoreRows] = useState([]);
  // Seeded from the session's remembered pick — a counter pharmacist works one
  // store all shift, and re-selecting it per order was pure friction.
  const [storeId, setStoreId] = useState(readPreferredPharmacyStore());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // What this order has already had dispensed, read from the dispensing service.
  // `ledgerError` is kept separate and shown rather than swallowed: falling back
  // to "nothing was dispensed" is precisely the state that causes a double fill.
  const [ledger, setLedger] = useState(null);
  const [ledgerError, setLedgerError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState({});
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [allergyChecked, setAllergyChecked] = useState(false);
  const [doseChecked, setDoseChecked] = useState(false);
  const [showPatientStrip, setShowPatientStrip] = useState(false);
  const summarySentinelRef = useRef(null);
  const [substituteDialog, setSubstituteDialog] = useState({
    open: false,
    lineId: null,
    loading: false,
    items: [],
    prescribedMedicine: '',
    genericQuery: '',
  });
  const [authModal, setAuthModal] = useState({ open: false, pendingLines: null });
  // The line being short-closed ("we cannot supply this"), or null.
  const [shortCloseLine, setShortCloseLine] = useState(null);
  const [shortClosing, setShortClosing] = useState(false);
  const [bulkCloseOpen, setBulkCloseOpen] = useState(false);
  // For each NOT_LISTED medicine name (keyed by medicineNameKey): does an item of
  // that name exist, and what main_medicine knows about it. Checked once per name
  // per visit, because the answer does not depend on the store.
  const [medicineOrigin, setMedicineOrigin] = useState({});
  const originRequestedRef = useRef(new Set());
  const mountedRef = useRef(true);
  const { can, modules } = useInvRole();
  // Same gate as the item form's route. A button that lands on "no access" is
  // worse than no button.
  const canCreateItem = modules.material && can(INV_ACTIONS.ITEM_MANAGE);

  const selectedStore = useMemo(
    () => storeRows.find((s) => String(s.store_id || s.id) === String(storeId)),
    [storeRows, storeId],
  );

  const searchScope = useMemo(() => ({
    tenant_id: prescription?.tenant_id || selectedStore?.tenant_id,
    hospital_id: prescription?.hospital_id || selectedStore?.hospital_id,
    location_id: storeId,
  }), [prescription, selectedStore, storeId]);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const rx = await getPrescription(prescriptionId);
        if (!alive) return;
        const mapped = mapPrescription(rx);
        const rxLines = mapPrescriptionLines(rx);
        setPrescription(mapped);

        // Has to resolve BEFORE the lines are built: a line built without it
        // offers the full prescribed quantity again, which is the double-dispense
        // this whole check exists to stop.
        let orderLedger = null;
        try {
          orderLedger = await loadOrderDispenseLedger(mapped);
          if (!alive) return;
          setLedgerError(null);
        } catch (e) {
          if (!alive) return;
          setLedgerError(e?.message || 'Could not read this order’s earlier dispenses');
        }
        setLedger(orderLedger);
        setLines(buildLinesFromItems(rxLines, orderLedger));
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load prescription');
      } finally {
        if (alive) setLoading(false);
      }

      try {
        const storeData = await getStores({ is_pharmacy_store: true });
        if (!alive) return;
        const storeList = Array.isArray(storeData) ? storeData : (storeData?.items || []);
        setStoreRows(storeList);
      } catch {
        if (alive) setStoreRows([]);
      }
    }

    load();
    return () => { alive = false; };
  }, [prescriptionId]);

  const storeOptions = useMemo(
    () => storeRows.map((s) => ({ value: s.store_id || s.id, label: s.store_name })),
    [storeRows],
  );

  // Validate the remembered store once the list arrives — a stale id from a
  // previous facility must not silently point stock checks at nothing. With
  // exactly one pharmacy store there is nothing to choose, so choose it.
  useEffect(() => {
    if (!storeRows.length) return;
    const exists = storeRows.some((s) => String(s.store_id || s.id) === String(storeId));
    if (storeId && !exists) {
      setStoreId('');
    } else if (!storeId && storeRows.length === 1) {
      const only = String(storeRows[0].store_id || storeRows[0].id);
      setStoreId(only);
      writePreferredPharmacyStore(only);
    }
  }, [storeRows, storeId]);

  const lineSignature = useMemo(
    () => lines.map((l) => `${l.id}:${l.medicine}:${l.generic_name || ''}`).join('|'),
    [lines],
  );

  useEffect(() => {
    if (!storeId || !lines.length) {
      setAvailability({});
      return;
    }

    const selectedStore = storeRows.find((s) => String(s.store_id || s.id) === String(storeId));
    const tenantId = prescription?.tenant_id || selectedStore?.tenant_id;
    const hospitalId = prescription?.hospital_id || selectedStore?.hospital_id;
    const lineSnapshot = lines.map((l) => ({
      id: l.id,
      medicine: l.medicine,
      generic_name: l.generic_name,
    }));

    let alive = true;
    setAvailability(Object.fromEntries(
      lineSnapshot.map((l) => [l.id, { loading: true, status: null, item: null, message: null }]),
    ));

    Promise.all(lineSnapshot.map((line) => resolvePrescribeLineStock({
      line,
      tenantId,
      hospitalId,
      storeId,
    }))).then((results) => {
      if (!alive) return;
      setAvailability(Object.fromEntries(
        results.map((result) => [result.lineId, {
          loading: false,
          status: result.status,
          item: result.item,
          message: result.message,
        }]),
      ));
      setLines((prev) => prev.map((row) => {
        const match = results.find((r) => r.lineId === row.id);
        if (!match || match.status !== STOCK_STATUS.IN_STOCK) return row;
        return applyMatchedItemToLine(row, match.item);
      }));
    });

    return () => { alive = false; };
  }, [storeId, lineSignature, prescription, storeRows]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const requestOriginCheck = useCallback((name) => {
    const key = medicineNameKey(name);
    if (!key) return;
    originRequestedRef.current.add(key);
    setMedicineOrigin((prev) => ({ ...prev, [key]: { status: MEDICINE_ORIGIN.CHECKING } }));
    resolveNewMedicineOrigin(name).then((result) => {
      if (mountedRef.current) setMedicineOrigin((prev) => ({ ...prev, [key]: result }));
    });
  }, []);

  // Only lines the catalogue could not find are checked, and only for users who
  // could act on the answer. Every other line costs no extra call.
  useEffect(() => {
    if (!canCreateItem) return;
    lines.forEach((line) => {
      const avail = availability[line.id];
      if (!avail || avail.loading || avail.status !== STOCK_STATUS.NOT_LISTED) return;
      const key = medicineNameKey(line.medicine);
      if (!key || originRequestedRef.current.has(key)) return;
      requestOriginCheck(line.medicine);
    });
  }, [canCreateItem, lines, availability, requestOriginCheck]);

  // The summary cards (patient / UHID / doctor / location) scroll out of view
  // exactly when the pharmacist starts typing quantities — the moment patient
  // identity matters most. Watch a sentinel under the cards and pin a compact
  // strip to the viewport top while they are off-screen.
  useEffect(() => {
    const node = summarySentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      setShowPatientStrip(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading]);

  const patientAlerts = useMemo(() => {
    if (!prescription?._raw) return [];
    return extractPatientClinicalAlerts(prescription._raw);
  }, [prescription]);

  // Dispenses already raised against this order. Shown even when every line is
  // still fillable — "there is no trace of it anywhere" is half the problem.
  const priorDispenses = ledger?.transactions || [];

  // Lines this store cannot supply, grouped by the reason their stock status
  // implies. The check has already established the fact; recording it used to
  // cost four clicks per line.
  const shortCloseGroups = storeId ? groupLinesForShortClose(lines, availability) : [];
  const bulkCloseCount = shortCloseLineCount(shortCloseGroups);

  const notListedCount = storeId
    ? lines.filter((l) => availability[l.id]?.status === STOCK_STATUS.NOT_LISTED).length
    : 0;

  const outOfStockCount = storeId
    ? lines.filter((l) => availability[l.id]?.status === STOCK_STATUS.OUT_OF_STOCK).length
    : 0;

  const missingUomCount = storeId
    ? lines.filter((l) => {
      const avail = availability[l.id];
      return l.selected && avail && !avail.loading && avail.item && !l.uom_id;
    }).length
    : 0;

  const selectedLines = lines.filter((l) => l.selected && Number(l.dispense_qty) > 0);
  // Selected but with no usable quantity. These used to fall out of `selectedLines`
  // silently and simply not be dispensed — the pharmacist got no error, just a short
  // delivery. Now they block the submit until someone types a number.
  const linesNeedingQty = lines.filter((l) => l.selected && !(Number(l.dispense_qty) > 0));
  // Asking for more than the order still owes is the double-dispense in its
  // smallest form — one line at a time instead of one order at a time. The
  // quantity box already shows this as a field error; without a submit gate the
  // pharmacist could reserve the excess anyway.
  //
  // Enforced only where the prescribed number is REAL and counted in the unit
  // being typed — the same two conditions as `prescribedCap` in the table below,
  // because against an estimate, or across units, the comparison means nothing.
  const linesOverRemaining = lines.filter((l) => {
    if (!l.selected || !l.qty_specified) return false;
    const issueUomId = availability[l.id]?.item?.issue_uom_id;
    if (issueUomId && l.uom_id && String(l.uom_id) !== String(issueUomId)) return false;
    return Number(l.dispense_qty) > l.prescribed_qty - (l.dispensed_qty || 0);
  });
  const controlledSelectedCount = selectedLines.filter((l) => isControlledOrNarcotic(l)).length;
  const estimatedTotal = selectedLines.reduce((sum, l) => {
    const rate = Number(l.unit_rate) || 0;
    return sum + rate * Number(l.dispense_qty);
  }, 0);

  // One line in the footer telling the pharmacist why submit is disabled —
  // mirrors handleSubmit's checks in the order they would fire.
  const footerHint = !storeId
    ? 'Select a pharmacy store to check stock'
    : linesNeedingQty.length
      ? `${linesNeedingQty.length} selected medicine(s) need a dispense quantity`
      : linesOverRemaining.length
        ? `${linesOverRemaining.length} medicine(s) exceed what this order still owes`
        : !selectedLines.length
          ? 'Select at least one medicine to dispense'
          : (!allergyChecked || !doseChecked)
            ? 'Complete allergy and dose checks to reserve stock'
            : estimatedTotal > 0
              ? `Estimated total ${formatCurrency(estimatedTotal)}`
              : null;

  function updateLine(id, patch) {
    setLines((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function applySubstitute(lineId, substitute) {
    enrichItemUom(substitute).then((enriched) => {
      setLines((prev) => prev.map((row) => {
        if (row.id !== lineId) return row;
        return applyMatchedItemToLine({
          ...row,
          medicine: enriched.item_name || row.medicine,
          selected: true,
          substitute_for_item_id: row.item_id || null,
          // Display-only: lets the row show a "Substituted" badge naming the
          // originally prescribed medicine after the name is overwritten above.
          substituted_from: row.substituted_from || row.medicine,
        }, enriched);
      }));
      setAvailability((prev) => ({
        ...prev,
        [lineId]: {
          ...prev[lineId],
          status: STOCK_STATUS.IN_STOCK,
          item: enriched,
          message: null,
        },
      }));
      setSubstituteDialog({
        open: false,
        lineId: null,
        loading: false,
        items: [],
        prescribedMedicine: '',
        genericQuery: '',
      });
    });
  }

  async function handleOpenSubstitute(line) {
    const genericQuery = prepareGenericSearchTerm(line.generic_name);
    if (!genericQuery) {
      toast.error('No generic name available for this medicine');
      return;
    }
    if (!storeId) {
      toast.error('Select a pharmacy store first');
      return;
    }

    setSubstituteDialog({
      open: true,
      lineId: line.id,
      loading: true,
      items: [],
      prescribedMedicine: line.medicine,
      genericQuery,
    });

    try {
      const items = await fetchPrescribeSearch({
        ...searchScope,
        q: genericQuery,
      });
      setSubstituteDialog((prev) => (
        prev.lineId === line.id ? { ...prev, loading: false, items } : prev
      ));
    } catch {
      setSubstituteDialog((prev) => (
        prev.lineId === line.id ? { ...prev, loading: false, items: [] } : prev
      ));
      toast.error('Failed to load substitute medicines');
    }
  }

  // Opens the ordinary item form, pre-filled from main_medicine when it has the
  // medicine. The name stays exactly as prescribed, because that exact string is
  // what this screen searches the catalogue for.
  function handleCreateItem(line, masterRow) {
    history.push(`${INV_SUITE_BASE}/material/items/new`, {
      newMedicine: buildNewMedicineDraft(line, masterRow),
      returnTo: url,
    });
  }

  function handleCloseSubstituteDialog() {
    setSubstituteDialog({
      open: false,
      lineId: null,
      loading: false,
      items: [],
      prescribedMedicine: '',
      genericQuery: '',
    });
  }

  /**
   * CPOE order book: the pharmacist has committed to filling this order.
   *
   * This used to fire from the load effect, which made a *read* perform a
   * *write* — merely opening the page moved the order to Acknowledged, and the
   * backend chain is forward-only, so there was no way back. Orders read as
   * "In Progress" because someone once looked at them.
   *
   * Best-effort and non-blocking: the dispense itself must still proceed. But a
   * failure has to be visible — a swallowed rejection (order cancelled
   * meanwhile) left the list showing Sent while the pharmacist believed the
   * order was theirs. Skipped for demo rows with no order identity, and for
   * orders already past Sent, where the POST would be a no-op.
   */
  function acknowledgeOrder() {
    if (!prescription?.admission_id || !prescription?.order_number) return;
    if (!isPendingPrescriptionStatus(prescription.status)) return;
    updateIpdPrescriptionStatus({
      admission_id: prescription.admission_id,
      order_no: prescription.order_number,
      status: 'Acknowledged',
    }).catch((err) => {
      toast.warn(err?.message
        ? `Could not mark this order acknowledged: ${err.message}`
        : 'Could not mark this order acknowledged.');
    });
  }

  /**
   * "We cannot supply this line" — ShortClosed for ONE medicine row, so the
   * rest of the order stays fillable. Backend requires a reason and retires the
   * line's remaining dose schedule on the ward (order_lifecycle.trim_dose_tasks),
   * which is the whole point: without it the nurse keeps waiting for stock that
   * will never come. A 200 "Success" can still decline the row — surface every
   * rejected reason instead of pretending it worked.
   */
  async function submitLineShortClose(reason) {
    const line = shortCloseLine;
    if (!line || !prescription?.admission_id) return;
    setShortClosing(true);
    try {
      const res = await updateIpdPrescriptionStatus({
        admission_id: prescription.admission_id,
        prescription_ids: [Number(line.id)],
        status: 'ShortClosed',
        reason,
      });
      const outcome = statusUpdateOutcome(res);
      if (outcome.rejected.length) toast.warn(rejectedReasonsText(outcome.rejected));
      if (outcome.ok) {
        toast.success(`${line.medicine || 'Line'} short-closed — ${reason}`);
        setShortCloseLine(null);
        const remaining = lines.filter((l) => l.id !== line.id);
        setLines(remaining);
        // Nothing left to dispense on this order — back to the queue rather
        // than an empty medicines table.
        if (!remaining.length) history.push(backUrl);
      } else if (!outcome.rejected.length) {
        toast.error('The line could not be short-closed.');
      }
    } catch (e) {
      toast.error(e?.message || 'Could not short-close the line.');
    } finally {
      setShortClosing(false);
    }
  }

  /**
   * Close every unavailable line in one action.
   *
   * One request PER GROUP, not per line: the endpoint takes a single reason for
   * the whole prescription_ids array, so lines that are out of stock and lines
   * that are not in the formulary cannot share a call without one of them being
   * recorded under a cause that is not true.
   *
   * Sequential rather than Promise.all — these are writes against rows of the
   * same order, and a half-applied bulk action is far easier to explain when the
   * groups went in one at a time. A 200 "Success" can still decline rows, so
   * every rejected reason is surfaced (same rule as the per-line path).
   */
  async function submitBulkShortClose(groups) {
    if (!prescription?.admission_id) return;
    setShortClosing(true);
    try {
      const responses = [];
      for (const group of groups) {
        // eslint-disable-next-line no-await-in-loop
        responses.push(await updateIpdPrescriptionStatus({
          admission_id: prescription.admission_id,
          prescription_ids: group.ids,
          status: 'ShortClosed',
          reason: group.reason,
        }));
      }

      const outcome = mergeStatusUpdateOutcomes(responses);
      if (outcome.rejected.length) toast.warn(rejectedReasonsText(outcome.rejected));

      const closedIds = new Set(groups.flatMap((g) => g.ids).map(String));
      const stillRejected = new Set(outcome.rejected.map((r) => String(r?.id)));
      const removed = [...closedIds].filter((id) => !stillRejected.has(id));

      if (removed.length) {
        toast.success(`${removed.length} medicine(s) marked not supplied — ward schedule retired`);
        const remaining = lines.filter((l) => !removed.includes(String(l.id)));
        setLines(remaining);
        setBulkCloseOpen(false);
        // Nothing left to dispense on this order — back to the queue rather
        // than an empty medicines table.
        if (!remaining.length) history.push(backUrl);
      } else if (!outcome.rejected.length) {
        toast.error('The lines could not be marked not supplied.');
      }
    } catch (e) {
      toast.error(e?.message || 'Could not mark the lines not supplied.');
    } finally {
      setShortClosing(false);
    }
  }

  async function submitDispense(enrichedLines, pharmacistAuthorization = null) {
    const hasControlled = enrichedLines.some((l) => isControlledOrNarcotic(l));
    acknowledgeOrder();

    if (hasControlled) {
      const { transactionId, pendingControlledAuth } = await runCreateVerifyControlled({
        prescription,
        storeId,
        lines: enrichedLines,
        stores: storeRows,
        allergyChecked,
        doseChecked,
        verificationNotes: 'Prescription and dose verified at pharmacy',
        remarks: `Dispense for ${prescription.order_number || prescription.id}`,
        pharmacistAuthorization,
        authReason: `Controlled dispense for ${prescription.order_number || prescription.id}`,
      });
      if (pendingControlledAuth) {
        toast.success(
          `Dispense created — ${controlledSelectedCount} controlled medicine(s) awaiting supervisor approval in Controlled Queue`,
        );
      } else {
        toast.success(`Stock reserved for ${prescription.order_number || prescription.id}`);
      }
      history.push(`${url.replace(`/start-dispense/${prescriptionId}`, '')}/dispenses/${transactionId}`);
      return;
    }

    const { transactionId } = await runCreateVerifyReserve({
      prescription,
      storeId,
      lines: enrichedLines,
      stores: storeRows,
      allergyChecked,
      doseChecked,
      verificationNotes: 'Prescription and dose verified at pharmacy',
      remarks: `Dispense for ${prescription.order_number || prescription.id}`,
      pharmacistAuthorization,
    });
    toast.success(
      `Stock reserved for ${prescription.order_number || prescription.id} — ${enrichedLines.length} medicine(s)`,
    );
    history.push(`${url.replace(`/start-dispense/${prescriptionId}`, '')}/dispenses/${transactionId}`);
  }

  async function handleSubmit() {
    if (!storeId) {
      toast.error('Select a pharmacy store');
      return;
    }
    // Checked before the emptiness test: when every selected line is missing a
    // quantity, `selectedLines` is empty and "select a medicine" would be the wrong
    // advice — they did select one.
    if (linesNeedingQty.length) {
      toast.error(`Enter a dispense quantity for ${linesNeedingQty.length} selected medicine(s)`);
      return;
    }
    if (linesOverRemaining.length) {
      toast.error(
        `${linesOverRemaining.length} medicine(s) are set above what this order still owes. `
        + 'Reduce them to the Pending quantity before reserving stock.',
      );
      return;
    }
    if (!selectedLines.length) {
      toast.error('Select at least one medicine to dispense');
      return;
    }
    if (!allergyChecked || !doseChecked) {
      toast.error('Confirm allergy and dose checks before reserving stock');
      return;
    }

    if (controlledSelectedCount > 0) {
      setAuthModal({ open: true, pendingLines: selectedLines });
      return;
    }

    setSubmitting(true);
    try {
      await submitDispense(selectedLines);
    } catch (e) {
      toast.error(e?.message || 'Failed to create dispense');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAuthConfirm({ licenseNumber, confirmed }) {
    if (!confirmed) {
      toast.error('Confirm the authorization checkbox to continue');
      return;
    }

    setSubmitting(true);
    try {
      const pharmacistAuthorization = buildPharmacistAuthorization(null, licenseNumber);
      await submitDispense(authModal.pendingLines, pharmacistAuthorization);
      setAuthModal({ open: false, pendingLines: null });
    } catch (e) {
      toast.error(e?.message || 'Failed to create dispense');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
        <Typography sx={{ color: IPD_COLORS.textSecondary }}>Loading prescription…</Typography>
      </Box>
    );
  }

  if (error || !prescription) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ borderRadius: '10px', mb: 2 }}>{error || 'Prescription not found.'}</Alert>
        <InvButton variant="outlined" tone="teal" onClick={() => history.push(backUrl)}>Back to Active Dispenses</InvButton>
      </Box>
    );
  }

  if (lines.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info" sx={{ borderRadius: '10px', mb: 2 }}>All medicines on this prescription have already been dispensed.</Alert>
        {priorDispenses.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {priorDispenses.map((txn) => (
              <InvButton
                key={txn.id}
                variant="outlined"
                tone="teal"
                size="small"
                sx={{ mr: 1, mb: 1 }}
                onClick={() => history.push(`${url.replace(`/start-dispense/${prescriptionId}`, '')}/dispenses/${txn.id}`)}
              >
                {txn.transaction_number || txn.id}
              </InvButton>
            ))}
          </Box>
        )}
        <InvButton variant="outlined" tone="teal" startIcon={<ArrowBackIcon />} onClick={() => history.push(backUrl)}>
          Back to Active Dispenses
        </InvButton>
      </Box>
    );
  }

  return (
    <Box sx={fadeInSx}>
      {/* Zero-height sticky shell: showing/hiding the strip never shifts the page. */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        height: 0,
        overflow: 'visible',
        mx: { xs: -2, sm: -2.5, md: -3 },
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          columnGap: 1,
          rowGap: 0.25,
          px: { xs: 2, sm: 2.5, md: 3 },
          py: 1.1,
          backgroundColor: IPD_COLORS.cardBg,
          borderBottom: `1px solid ${IPD_COLORS.border}`,
          boxShadow: IPD_COLORS.shadow.card,
          opacity: showPatientStrip ? 1 : 0,
          transform: showPatientStrip ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'opacity 0.15s ease, transform 0.15s ease',
          pointerEvents: showPatientStrip ? 'auto' : 'none',
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: IPD_COLORS.textPrimary }}>
            {prescription.patient_name}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
            {[
              prescription.uhid || prescription.order_number,
              prescription.doctor_name,
              prescription.ward,
            ].filter(Boolean).map((part) => `· ${part}`).join(' ')}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'stretch', gap: 2.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InvButton variant="outlined" tone="teal" size="small" startIcon={<ArrowBackIcon />}
                onClick={() => history.push(backUrl)}>
                Back
              </InvButton>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 20, color: IPD_COLORS.textPrimary }}>Start Dispense</Typography>
                <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                  {prescription.order_number || prescription.uhid || prescription.id} · {prescription.patient_name}
                </Typography>
              </Box>
            </Box>
            <InvButton
              loading={submitting}
              onClick={handleSubmit}
              disabled={!selectedLines.length || linesNeedingQty.length > 0
                || linesOverRemaining.length > 0 || !allergyChecked || !doseChecked}
            >
              Verify & Reserve Stock
            </InvButton>
          </Box>

          <DetailSummaryGrid>
            <Grid item xs={6} sm={3}><SummaryStat label="Patient" value={prescription.patient_name} /></Grid>
            <Grid item xs={6} sm={3}><SummaryStat label="UHID" value={prescription.uhid || prescription.order_number} /></Grid>
            <Grid item xs={6} sm={3}><SummaryStat label="Doctor" value={prescription.doctor_name} /></Grid>
            <Grid item xs={6} sm={3}><SummaryStat label="Location" value={prescription.ward} /></Grid>
          </DetailSummaryGrid>
          <Box ref={summarySentinelRef} aria-hidden sx={{ height: 0 }} />

          {(prescription.provisional_diagnosis || prescription.final_diagnosis) && (
            <Box sx={{ mb: 2 }}>
              {prescription.provisional_diagnosis && (
                <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 0.25 }}>
                  <strong>Provisional diagnosis:</strong> {prescription.provisional_diagnosis}
                </Typography>
              )}
              {prescription.final_diagnosis && (
                <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                  <strong>Final diagnosis:</strong> {prescription.final_diagnosis}
                </Typography>
              )}
            </Box>
          )}

          <SectionCard title="Pharmacist verification" subtitle="Required before stock is reserved">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={allergyChecked}
                    onChange={(e) => setAllergyChecked(e.target.checked)}
                    sx={{ color: IPD_COLORS.primary, '&.Mui-checked': { color: IPD_COLORS.primary } }}
                  />
                )}
                label="Allergy check completed"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={doseChecked}
                    onChange={(e) => setDoseChecked(e.target.checked)}
                    sx={{ color: IPD_COLORS.primary, '&.Mui-checked': { color: IPD_COLORS.primary } }}
                  />
                )}
                label="Dose check completed"
              />
            </Box>
            {patientAlerts.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '10px' }}>
                {patientAlerts.length} patient clinical alert(s) — review before verifying.
              </Alert>
            )}
          </SectionCard>

          {ledgerError && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
              {ledgerError}. The Pending column below may not account for medicines
              already handed over — open this order’s dispenses before reserving stock.
            </Alert>
          )}

          {priorDispenses.length > 0 && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>
                {priorDispenses.length} dispense(s) already raised for this order —
                Pending below is what is still owed.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {priorDispenses.map((txn) => (
                  <InvButton
                    key={txn.id}
                    variant="outlined"
                    tone="teal"
                    size="small"
                    onClick={() => history.push(`${url.replace(`/start-dispense/${prescriptionId}`, '')}/dispenses/${txn.id}`)}
                  >
                    {`${txn.transaction_number || txn.id} · ${txn.qty} unit(s)${txn.dispense_stage ? ` · ${txn.dispense_stage}` : ''}${txn.created_at ? ` · ${formatDate(txn.created_at)}` : ''}`}
                  </InvButton>
                ))}
              </Box>
            </Alert>
          )}

          <SectionCard title="Medicines to Dispense" subtitle="Select items, enter batch (or leave blank for FEFO) and quantity">
            <Box sx={{ mb: 2, maxWidth: 320 }}>
              <InvSelect
                label="Pharmacy Store *"
                value={storeId}
                onChange={(e) => {
                  setStoreId(e.target.value);
                  writePreferredPharmacyStore(e.target.value);
                }}
                options={storeOptions}
              />
            </Box>

            {bulkCloseCount > 1 && (
              <Alert
                severity="warning"
                sx={{ mb: 2, borderRadius: '10px' }}
                action={(
                  <InvButton size="small" tone="amber" onClick={() => setBulkCloseOpen(true)}>
                    Mark all as not supplied
                  </InvButton>
                )}
              >
                {bulkCloseCount} medicine(s) cannot be supplied from this store.
                Closing them together retires the ward's dose schedule in one step —
                the rest of the order stays fillable.
              </Alert>
            )}

            {linesNeedingQty.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
                {linesNeedingQty.length} selected medicine(s) have no dispense quantity.
                The prescriber did not state one, so enter it before reserving stock.
              </Alert>
            )}

            <Box sx={{ ...lineTableSx, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell sx={{ minWidth: 200 }}>Medicine</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Generic</TableCell>
                    <TableCell sx={{ width: 90 }}>Pending</TableCell>
                    <TableCell sx={{ width: 150 }}>Dispense Qty</TableCell>
                    <TableCell sx={{ width: 150 }}>Batch</TableCell>
                    <TableCell sx={{ width: 90 }}>MRP</TableCell>
                    <TableCell sx={{ width: 100 }}>Expiry</TableCell>
                    <TableCell sx={{ width: 100 }} align="right">Available Qty</TableCell>
                    <TableCell sx={{ width: 110 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line) => {
                    const avail = availability[line.id];
                    const checking = !!avail?.loading;
                    const matchedItem = avail?.item || null;
                    const status = avail?.status;
                    // available_quantity is in the item's BASE unit; the box
                    // below takes the ISSUE unit. issueQtyIssue owns the
                    // comparison so the two are never pitted against each other.
                    const availableQty = matchedItem && status === STOCK_STATUS.IN_STOCK
                      ? Number(matchedItem.available_quantity) || 0
                      : null;
                    // Whole packs the shelf can actually make up, each batch at
                    // its own size. floor(base total / the item's factor) is a
                    // different, wrong number as soon as one GRN line froze a
                    // pack size of its own.
                    const issuableQty = availableQty != null
                      ? totalIssuableQty(matchedItem.batches, matchedItem)
                      : null;
                    const factor = issueUomFactor(matchedItem);
                    const issueUnit = issueUomCode(matchedItem);
                    const baseUnit = baseUomCode(matchedItem);
                    const prescribedRemaining = line.qty_specified
                      ? line.prescribed_qty - (line.dispensed_qty || 0)
                      : null;
                    // An earlier dispense counted in a different unit cannot be
                    // subtracted from this line — 2 BOX and 20 TAB are not
                    // comparable numbers, so Pending is reported as unreliable
                    // rather than quietly computed from mismatched units.
                    const priorUomMismatch = priorDispenseUomMismatch(line.prior_dispense, line.uom_id);
                    // Only cap by the prescribed number when it is counted in
                    // the same unit the pharmacist is typing in — capping a
                    // strip count by a tablet count would block a valid dispense.
                    const prescribedCap = prescribedRemaining != null
                      && (!matchedItem?.issue_uom_id || !line.uom_id
                        || String(line.uom_id) === String(matchedItem.issue_uom_id))
                      ? prescribedRemaining
                      : undefined;
                    // Both caps are already in issue units — what the
                    // prescription still owes and what the shelf can make up
                    // into whole packs. The tighter one decides.
                    const caps = [prescribedCap, issuableQty].filter((n) => n != null);
                    const dispenseCap = caps.length ? Math.min(...caps) : undefined;
                    const qtyProblem = matchedItem && line.dispense_qty !== '' && line.dispense_qty != null
                      ? issueQtyIssue({
                        qty: line.dispense_qty,
                        factor,
                        code: issueUnit,
                        max: dispenseCap,
                      })
                      : null;
                    const short = !!qtyProblem;
                    const displayName = matchedItem?.item_name || line.medicine;
                    const displayGeneric = matchedItem?.generic_name || line.generic_name || '—';
                    // Priced per issue unit, to match the quantity beside it.
                    const displayMrp = matchedItem?.mrp_issue_uom
                      || matchedItem?.rate_issue_uom
                      || matchedItem?.mrp
                      || matchedItem?.rate;
                    const displayBatch = line.batch_number || matchedItem?.batch_number || '—';
                    const displayExpiry = matchedItem?.expiry_date ? formatDate(matchedItem.expiry_date) : '—';
                    const canSubstitute = !!storeId
                      && !checking
                      && status !== STOCK_STATUS.IN_STOCK
                      && !!prepareGenericSearchTerm(line.generic_name);
                    // "We cannot supply this" — only for lines the store cannot
                    // fill, and only for real order rows: a synthetic id
                    // ("rx-med-0") identifies nothing server-side, and an
                    // emergency script has no admission to address the write to.
                    const canShortCloseLine = !checking
                      && !!prescription?.admission_id
                      && /^\d+$/.test(String(line.id ?? '').trim())
                      && (status === STOCK_STATUS.OUT_OF_STOCK || status === STOCK_STATUS.NOT_LISTED);
                    // Create Item only once an exact-name lookup across the whole
                    // item master has come back empty, so no duplicate is offered.
                    const origin = status === STOCK_STATUS.NOT_LISTED
                      ? medicineOrigin[medicineNameKey(line.medicine)]
                      : null;
                    const originStatus = checking ? null : origin?.status;
                    const canCreateNewItem = canCreateItem
                      && originStatus === MEDICINE_ORIGIN.NOT_IN_INVENTORY;
                    const existingItemNote = originStatus === MEDICINE_ORIGIN.IN_INVENTORY
                      ? [origin.item?.item_code, origin.item?.is_active === false ? 'inactive' : '']
                        .filter(Boolean).join(', ')
                      : '';
                    const hasOriginAction = canCreateNewItem
                      || originStatus === MEDICINE_ORIGIN.CHECKING
                      || originStatus === MEDICINE_ORIGIN.FAILED;

                    return (
                      <React.Fragment key={line.id}>
                        <TableRow sx={{ opacity: isControlledOrNarcotic(line) ? 0.95 : 1 }}>
                          <TableCell sx={{ py: 1.75 }}>
                            <Checkbox
                              size="small"
                              checked={line.selected}
                              disabled={status === STOCK_STATUS.NOT_LISTED}
                              onChange={(e) => updateLine(line.id, { selected: e.target.checked })}
                              sx={{ color: IPD_COLORS.primary, '&.Mui-checked': { color: IPD_COLORS.primary } }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{displayName}</Typography>
                              {isControlledOrNarcotic(line) && (
                                <Tooltip title={`Controlled substance${line.schedule ? ` · Schedule ${line.schedule}` : ''} — requires authorization`}>
                                  <Chip
                                    icon={<LockIcon sx={{ fontSize: 13 }} />}
                                    label="Controlled"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: 10.5,
                                      fontWeight: 700,
                                      color: IPD_COLORS.warning.text,
                                      backgroundColor: IPD_COLORS.warning.bg,
                                      border: `1px solid ${IPD_COLORS.warning.border}`,
                                      '& .MuiChip-icon': { color: IPD_COLORS.warning.text, ml: 0.5 },
                                    }}
                                  />
                                </Tooltip>
                              )}
                              {line.substituted_from && (
                                <Tooltip title={`Substituted for ${line.substituted_from}`}>
                                  <Chip
                                    label="✓ Substituted"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: 10.5,
                                      fontWeight: 700,
                                      color: IPD_COLORS.success.text,
                                      backgroundColor: IPD_COLORS.success.bg,
                                      border: `1px solid ${IPD_COLORS.success.border}`,
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 0.35 }}>
                              {line.dosage} · {line.frequency} · {line.duration}
                            </Typography>
                            {status === STOCK_STATUS.NOT_LISTED && avail?.message && (
                              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted, mt: 0.5 }}>
                                {avail.message}
                              </Typography>
                            )}
                            {originStatus === MEDICINE_ORIGIN.IN_INVENTORY && (
                              <Typography sx={{ fontSize: 12, color: IPD_COLORS.warning.text, mt: 0.5 }}>
                                {`An item with this name already exists${existingItemNote ? ` (${existingItemNote})` : ''}, but it is not in this store's pharmacy catalogue.`}
                              </Typography>
                            )}
                            {originStatus === MEDICINE_ORIGIN.FAILED && (
                              <Typography sx={{ fontSize: 12, color: IPD_COLORS.error.text, mt: 0.5 }}>
                                Could not check the item master for this medicine.
                              </Typography>
                            )}
                            {status === STOCK_STATUS.OUT_OF_STOCK && avail?.message && (
                              <Typography sx={{ fontSize: 12, color: IPD_COLORS.warning.text, mt: 0.5 }}>
                                {avail.message}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1.75, fontSize: 13 }}>{displayGeneric}</TableCell>
                          <TableCell sx={{ py: 1.75 }}>
                            {line.qty_specified ? prescribedRemaining : (
                              <>
                                —
                                <Typography sx={{ fontSize: 11, color: IPD_COLORS.warning.text, mt: 0.35 }}>
                                  Not specified by prescriber
                                </Typography>
                              </>
                            )}
                            {line.dispensed_qty > 0 && (
                              <Typography sx={{ fontSize: 11, color: IPD_COLORS.textMuted, mt: 0.35 }}>
                                {line.dispensed_qty} already dispensed
                                {line.qty_specified ? ` of ${line.prescribed_qty}` : ''}
                              </Typography>
                            )}
                            {priorUomMismatch && (
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: IPD_COLORS.warning.text, mt: 0.35 }}>
                                Earlier dispense used a different unit — verify before dispensing
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6 }}>
                              <InvTextField label="" type="number" value={line.dispense_qty}
                                sx={{ width: 92, flexShrink: 0 }}
                                error={qtyProblem ? true : undefined}
                                helperText={qtyProblem || baseQtyHint(line.dispense_qty, factor, baseUnit)}
                                // No max when nothing was prescribed — capping at the estimate
                                // stopped the pharmacist entering the real quantity.
                                inputProps={prescribedCap != null
                                  ? { min: 1, max: prescribedCap, step: 1 }
                                  : { min: 1, step: 1 }}
                                onChange={(e) => updateLine(line.id, { dispense_qty: e.target.value })}
                                // The box is prefilled with the full remainder, and a
                                // partial fill is the common case — without this the
                                // pharmacist clears it by hand on every such line.
                                onFocus={(e) => e.target.select()}
                                disabled={!line.selected || status === STOCK_STATUS.NOT_LISTED} />
                              {/* Dispense unit comes from the item master — shown
                                  so the number is unambiguous, never editable here. */}
                              {issueUnit && (
                                <Chip
                                  label={issueUnit}
                                  size="small"
                                  disabled
                                  sx={{ height: 24, fontSize: 10.5, fontWeight: 700, mt: 0.4 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 1.75 }}>
                            <InvTextField label="" value={line.batch_number} placeholder="FEFO auto"
                              sx={{ width: 132 }}
                              onChange={(e) => updateLine(line.id, { batch_number: e.target.value })}
                              disabled={!line.selected || status === STOCK_STATUS.NOT_LISTED} />
                            {status === STOCK_STATUS.IN_STOCK && !line.batch_number && matchedItem?.batch_number && (
                              <Typography sx={{ fontSize: 11, color: IPD_COLORS.textMuted, mt: 0.35 }}>
                                Suggested: {matchedItem.batch_number}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1.75, fontSize: 13 }}>
                            {displayMrp ? formatCurrency(displayMrp) : '—'}
                          </TableCell>
                          <TableCell sx={{ py: 1.75, fontSize: 13 }}>{displayExpiry}</TableCell>
                          <TableCell sx={{ py: 1.75 }} align="right">
                            {!storeId ? (
                              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>Select store</Typography>
                            ) : checking ? (
                              <CircularProgress size={14} sx={{ color: IPD_COLORS.primary }} />
                            ) : status === STOCK_STATUS.NOT_LISTED ? (
                              <Tooltip title="This medicine is not registered in the pharmacy catalogue">
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: IPD_COLORS.textMuted }}>
                                  Not in catalogue
                                </Typography>
                              </Tooltip>
                            ) : status === STOCK_STATUS.OUT_OF_STOCK ? (
                              <Tooltip title="Prescribed item is out of stock — choose a substitute below if available">
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: IPD_COLORS.warning.text }}>
                                  Out of stock
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography sx={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: short ? IPD_COLORS.error.text : IPD_COLORS.textPrimary,
                              }}>
                                {availableQty != null
                                  ? formatIssuableQty(matchedItem.batches, matchedItem)
                                  : '—'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1.75 }} align="right">
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                              {canCreateNewItem && (
                                <InvButton
                                  size="small"
                                  tone="teal"
                                  onClick={() => handleCreateItem(line, origin.masterRow)}
                                >
                                  Create Item
                                </InvButton>
                              )}
                              {originStatus === MEDICINE_ORIGIN.CHECKING && (
                                <CircularProgress size={14} sx={{ color: IPD_COLORS.primary }} />
                              )}
                              {originStatus === MEDICINE_ORIGIN.FAILED && (
                                <InvButton
                                  size="small"
                                  variant="outlined"
                                  tone="teal"
                                  onClick={() => requestOriginCheck(line.medicine)}
                                >
                                  Retry check
                                </InvButton>
                              )}
                              {canSubstitute && (
                                <InvButton
                                  size="small"
                                  variant="outlined"
                                  tone="teal"
                                  onClick={() => handleOpenSubstitute(line)}
                                >
                                  Substitute
                                </InvButton>
                              )}
                              {canShortCloseLine && (
                                <InvButton
                                  size="small"
                                  variant="outlined"
                                  tone="amber"
                                  onClick={() => setShortCloseLine(line)}
                                >
                                  Short-close
                                </InvButton>
                              )}
                              {!canSubstitute && !canShortCloseLine && !hasOriginAction && (
                                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>—</Typography>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {controlledSelectedCount > 0 && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>
                {controlledSelectedCount} controlled medicine{controlledSelectedCount === 1 ? '' : 's'} selected —
                pharmacist authorization required on submit; supervisor must approve in Controlled Queue before stock reserve.
              </Alert>
            )}

            {notListedCount > 0 && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: '10px' }}>
                {notListedCount} medicine{notListedCount === 1 ? '' : 's'} {notListedCount === 1 ? 'is' : 'are'} not
                listed in the pharmacy catalogue. Use the Substitute button to search by generic name.
              </Alert>
            )}

            {outOfStockCount > 0 && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>
                {outOfStockCount} medicine{outOfStockCount === 1 ? '' : 's'} {outOfStockCount === 1 ? 'is' : 'are'} out
                of stock at the selected store. Use the Substitute button to pick an alternative.
              </Alert>
            )}

            {missingUomCount > 0 && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>
                {missingUomCount} medicine{missingUomCount === 1 ? '' : 's'} matched stock but have no unit of measure.
                The system will try item master on submit; if it still fails, set base UOM on the item.
              </Alert>
            )}
          </SectionCard>
        </Box>

        <Box sx={actionStripSx}>
          <Tooltip title="Dispense Summary" placement="left">
            <Fab size="medium" onClick={() => setSummaryOpen(true)} sx={fabButtonSx(IPD_COLORS.primary)}>
              <Badge badgeContent={selectedLines.length} color="error" overlap="circular">
                <ReceiptLongIcon />
              </Badge>
            </Fab>
          </Tooltip>
          <Tooltip title="Patient Alerts" placement="left">
            <Fab size="medium" onClick={() => setAlertsOpen(true)}
              sx={fabButtonSx(patientAlerts.length > 0 ? IPD_COLORS.error.text : IPD_COLORS.success.text)}>
              <Badge badgeContent={patientAlerts.length} color="error" overlap="circular">
                <WarningAmberIcon />
              </Badge>
            </Fab>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 10,
        mt: 3,
        mx: { xs: -2, sm: -2.5, md: -3 },
        px: { xs: 2, sm: 2.5, md: 3 },
        py: 1.5,
        backgroundColor: IPD_COLORS.cardBg,
        borderTop: `1px solid ${IPD_COLORS.border}`,
        boxShadow: '0px -4px 16px rgba(0,0,0,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
      }}>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
            {selectedLines.length} of {lines.length} medicine{lines.length === 1 ? '' : 's'} ready to dispense
          </Typography>
          {footerHint && (
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 0.25 }}>
              {footerHint}
            </Typography>
          )}
        </Box>
        <InvButton
          loading={submitting}
          onClick={handleSubmit}
          disabled={!selectedLines.length || linesNeedingQty.length > 0
            || linesOverRemaining.length > 0 || !allergyChecked || !doseChecked}
        >
          Verify &amp; Reserve Stock
        </InvButton>
      </Box>

      <Dialog open={alertsOpen} onClose={() => setAlertsOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}>
        <IconButton size="small" onClick={() => setAlertsOpen(false)}
          sx={{ position: 'absolute', right: 10, top: 10, zIndex: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogContent sx={{ pt: 3, pb: 3 }}>
          <PatientClinicalAlertsBox alerts={patientAlerts} patientName={prescription.patient_name} />
        </DialogContent>
      </Dialog>

      <SubstitutePickerDialog
        open={substituteDialog.open}
        loading={substituteDialog.loading}
        items={substituteDialog.items}
        prescribedMedicine={substituteDialog.prescribedMedicine}
        searchTerm={substituteDialog.genericQuery}
        onSelect={(sub) => applySubstitute(substituteDialog.lineId, sub)}
        onClose={handleCloseSubstituteDialog}
      />

      <OrderReasonDialog
        open={!!shortCloseLine}
        title={`Short-close ${shortCloseLine?.medicine || 'line'}`}
        description="Marks this medicine as not supplied and retires its remaining dose schedule on the ward. The rest of the order stays open."
        reasons={PHARMACY_SHORT_CLOSE_REASONS}
        confirmLabel="Short-close"
        loading={shortClosing}
        defaultReason={shortCloseReasonForStock(availability[shortCloseLine?.id]?.status)}
        onConfirm={submitLineShortClose}
        onClose={() => setShortCloseLine(null)}
      />

      <BulkShortCloseDialog
        open={bulkCloseOpen}
        groups={shortCloseGroups}
        reasons={PHARMACY_SHORT_CLOSE_REASONS}
        loading={shortClosing}
        onConfirm={submitBulkShortClose}
        onClose={() => setBulkCloseOpen(false)}
      />

      <Dialog open={summaryOpen} onClose={() => setSummaryOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          Dispense Summary
          <IconButton size="small" onClick={() => setSummaryOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Box sx={summaryHighlightSx}>
            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 2 }}>
              {selectedLines.length} of {lines.length} medicines selected
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>Estimated total</Typography>
              <Typography sx={{ fontWeight: 700, color: IPD_COLORS.primary }}>
                {estimatedTotal > 0 ? formatCurrency(estimatedTotal) : '—'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <StatusChip status={prescription.status} domain="prescription" />
              {patientAlerts.length > 0 && <StatusChip status="FAILED" />}
            </Box>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, lineHeight: 1.6 }}>
              Flow: Create → Verify → Reserve. Allocate and Deliver happen on the dispense detail screen.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <ControlledDrugAuthModal
        open={authModal.open}
        onClose={() => setAuthModal({ open: false, pendingLines: null })}
        onConfirm={handleAuthConfirm}
        loading={submitting}
        lineCount={controlledSelectedCount || 1}
        requiresSupervisorApproval
      />
    </Box>
  );
}
